import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ProgressCircle, Text } from '@adobe/react-spectrum';
import { getTooltipPortalTarget } from './popoverPositioning';
import {
  fetchFeedbackThread,
  postFeedbackThreadEntry
} from '../feedbackThreadApi';
import { FeedbackSlideoutNavigation, FeedbackThreadResponse } from '../types';
import { FeedbackSlideOutHeader } from './FeedbackSlideOutHeader';
import { NewFeedbackEditor } from './NewFeedbackEditor';
import { FeedbackThreadEntries } from './FeedbackThreadEntries';
import slideOutStyles from 'app/components/SlideOutPanel/SlideOutPanel.module.scss';
import styles from './FeedbackSlideOut.module.scss';
import bodyStyles from './FeedbackSlideOutBody.module.scss';

interface FeedbackSlideOutProps {
  navigation: FeedbackSlideoutNavigation | null;
  onClose: () => void;
  onFeedbackRead?: () => void | Promise<void>;
}

/**
 * Feedback slide-out - Review & Approval's own panel, opened from any
 * table's Feedback icon (see FeedbackCell.tsx). Deliberately NOT the
 * existing Release Moment/RSD slide-out: `useSlideoutUrl`'s open/close
 * state is a single URL-derived `issueId` (see its own investigation notes)
 * that can't represent a second, independently-open panel, so this owns
 * its own `context` state instead (lifted into ReviewApproval/index.tsx).
 *
 * Reuses the Moment slide-out's own drawer/backdrop chrome
 * (SlideOutPanel.module.scss's `.slideOutPanel`/`.open`) and portal target
 * (popoverPositioning.ts's getTooltipPortalTarget - the same
 * `[data-omega-feature="shell header"]` escape hatch SlideOutPanel itself
 * portals to, for the same Spectrum-CSS-custom-property-inheritance reason
 * documented there) for visual/behavioral parity, without importing the
 * SlideOutPanel component itself.
 *
 * `context` (header data) comes straight off the clicked row - no second
 * Moment/Milestone GET. The feedback-thread body is fetched here, via
 * `GET /api/v2/approvals/{issueId}/{milestoneId}/feedback-thread
 * ?milestoneName={name}&requestId={requestId}` (requestId is optional; see
 * feedbackThreadApi.ts) - mirroring the same
 * plain-service + useEffect-keyed-on-id pattern this app's other slide-out
 * tabs already use (FeaturesContainer.tsx/ChangeLog.tsx), not a new fetch
 * architecture. Keyed on `issueId`/`milestoneId`/`milestoneName`/`requestId`
 * so navigating between approval requests also re-fetches the thread.
 * `thread.entries` renders in API order via FeedbackThreadEntries and
 * FeedbackThreadEntryCard (one card per entry); `NewFeedbackEditor` is the
 * "NEW FEEDBACK" box, reusing the same QuillEditor component the RSD
 * slide-out's own Overview section already uses for rich text.
 *
 * `.mainContent` (SlideOutPanel.module.scss) wraps the header+body below,
 * same as the real SlideOutPanel does around its own Header+tab content -
 * `.slideOutPanel` itself is a *row*-direction flex container (it lays the
 * panel out next to the separate comment-panel sidebar), so without this
 * inner column wrapper the header and body would stretch to the panel's
 * full height and sit side-by-side instead of stacked, and `.header`'s own
 * full-height stretch would in turn make its `.separator` divider (which
 * is `height: 100%` of its own parent) run the full slide-out height
 * instead of just the header row.
 *
 * `isFullScreen` reuses the same `.fullScreen` modifier class Header's own
 * maximize button toggles on `.slideOutPanel` in the real SlideOutPanel -
 * this is its own independent state, not shared with the Moment slide-out.
 *
 * `navigation.onNavigatePrevious`/`onNavigateNext` (see
 * FeedbackSlideoutNavigation in ../types.ts) are built by whichever table
 * opened this panel, from its own currently-displayed row order - clicking
 * one calls back up through the table's own `onOpenFeedback` prop with a
 * new navigation object for the sibling row, which lands here as a new
 * `navigation` prop value; the fetch effect below is keyed on
 * `issueId`/`milestoneId`/`milestoneName`/`requestId`, so it re-fetches
 * whenever the sibling row belongs to a different Moment/milestone/request, the
 * same as a fresh open would.
 */
export function FeedbackSlideOut({
  navigation,
  onClose,
  onFeedbackRead
}: FeedbackSlideOutProps) {
  const context = navigation?.context ?? null;
  const issueId = context?.releaseMoment.issueId ?? null;
  const milestoneId = context?.milestoneId ?? null;
  const milestoneName = context?.milestoneName ?? null;
  const requestId = context?.requestId;

  const [thread, setThread] = useState<FeedbackThreadResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const threadGeneration = useRef(0);
  const closingGeneration = useRef<number | null>(null);

  useEffect(() => {
    threadGeneration.current += 1;
    if (!issueId || !milestoneId || !milestoneName) {
      setThread(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setThread(null);
    setIsLoading(true);
    setError(null);

    fetchFeedbackThread({ issueId, milestoneId, milestoneName, requestId })
      .then(result => {
        if (cancelled) return;
        if (result.success && result.data) {
          setThread(result.data);
        } else {
          setThread(null);
          setError(result.message || 'Could not load feedback.');
        }
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setThread(null);
        setError(err?.message || 'Could not load feedback.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      threadGeneration.current += 1;
    };
  }, [issueId, milestoneId, milestoneName, requestId]);

  // Reset full-screen on close so the next open (any row) always starts
  // back at the normal panel width, not whatever a previous open left it.
  // Navigating to a sibling row (a new `navigation` object, `context`
  // still non-null) does NOT reset this - full-screen should persist
  // across Prev/Next, the same way it persists across RSD navigation in
  // the real Moment slide-out.
  useEffect(() => {
    if (!context) setIsFullScreen(false);
  }, [context]);

  // Passed to NewFeedbackEditor as `onSend` - throws on failure (same
  // convention as fetchFeedbackThread/patchFeedbackThreadEntry's own
  // callers), so NewFeedbackEditor's own try/catch decides the
  // loading/error UI and keeps the typed draft on rejection. On success,
  // the response entry is prepended to `thread.entries` here (the single
  // source of truth the list below renders from) - de-duplicated by
  // `feedbackId` in case this ever resolves twice for the same entry.
  async function handleSendFeedback(content: string): Promise<void> {
    if (!issueId || !milestoneId || !milestoneName) {
      throw new Error('Missing moment/milestone context for sending feedback.');
    }
    const newEntry = await postFeedbackThreadEntry(
      { issueId, milestoneId, milestoneName },
      content
    );
    setThread(prev => {
      if (!prev) return prev;
      const withoutDuplicate = prev.entries.filter(
        entry =>
          entry.type !== 'FEEDBACK' || entry.feedbackId !== newEntry.feedbackId
      );
      return {
        ...prev,
        entries: [{ ...newEntry, type: 'FEEDBACK' }, ...withoutDuplicate]
      };
    });
  }

  // Refresh only threads with actual feedback (requestor notes do not count).
  // Ignore a late close response if the user navigated away or unmounted.
  async function handleClose(): Promise<void> {
    const generation = threadGeneration.current;
    if (closingGeneration.current === generation) return;

    if (
      !thread?.entries.some(entry => entry.type === 'FEEDBACK') ||
      !issueId ||
      !milestoneId ||
      !milestoneName
    ) {
      onClose();
      return;
    }

    closingGeneration.current = generation;
    try {
      const result = await fetchFeedbackThread({
        issueId,
        milestoneId,
        milestoneName,
        requestId
      });
      if (
        threadGeneration.current === generation &&
        result.success &&
        result.data
      ) {
        setThread(result.data);
        await onFeedbackRead?.();
      }
    } catch {
      // A refresh failure must not prevent closing the panel.
    } finally {
      if (closingGeneration.current === generation)
        closingGeneration.current = null;
      if (threadGeneration.current === generation) onClose();
    }
  }

  if (!navigation || !context) return null;

  return createPortal(
    <>
      <div
        data-testid="feedback-slideout-overlay"
        className={styles.overlay}
        onClick={() => void handleClose()}
      />
      <div
        data-testid="feedback-slideout-panel"
        className={[
          slideOutStyles.slideOutPanel,
          slideOutStyles.open,
          isFullScreen ? slideOutStyles.fullScreen : '',
          styles.panelAboveSlideOut
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className={`${slideOutStyles.mainContent} ${styles.mainContent}`}>
          <FeedbackSlideOutHeader
            context={context}
            onClose={() => void handleClose()}
            isFullScreen={isFullScreen}
            onToggleFullScreen={() => setIsFullScreen(prev => !prev)}
            onNavigatePrevious={navigation.onNavigatePrevious}
            onNavigateNext={navigation.onNavigateNext}
          />
          <div className={styles.body} data-testid="feedback-slideout-body">
            <div className={styles.bodyContent}>
              {isLoading && (
                <div className={styles.statusMessage}>
                  <ProgressCircle
                    size="S"
                    aria-label="Loading feedback"
                    isIndeterminate
                  />
                  <Text>Loading feedback…</Text>
                </div>
              )}
              {!isLoading && error && (
                <div
                  className={`${styles.statusMessage} ${styles.errorMessage}`}
                  data-testid="feedback-slideout-error"
                >
                  {error}
                </div>
              )}
              {!isLoading &&
                !error &&
                thread &&
                thread.entries.length === 0 && (
                  <div
                    className={styles.statusMessage}
                    data-testid="feedback-slideout-empty"
                  >
                    No feedback yet
                  </div>
                )}
              {!isLoading && !error && thread && (
                <div className={bodyStyles.notesSection}>
                  <h2 className={bodyStyles.sectionTitle}>
                    Notes and Feedback
                  </h2>
                  <NewFeedbackEditor
                    emailRecipients={thread.emailRecipients}
                    onSend={handleSendFeedback}
                  />
                  <FeedbackThreadEntries entries={thread.entries} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    getTooltipPortalTarget()
  );
}

export default FeedbackSlideOut;
